import { useEffect } from "react";
import { Link, useParams } from "wouter";
import { getPostBySlug } from "@/data/blog-posts";

const TEAL = "#6FAFB5";
const NAVY = "#1F2E3B";
const CREAM = "#F7F4EE";
const MUTED = "#536270";
const GOLD = "#E5B75E";

function Nav() {
  return (
    <nav style={{ background: "rgba(247,244,238,.92)", backdropFilter: "blur(14px)", borderBottom: "1px solid #e4ddd2", position: "sticky", top: 0, zIndex: 50 }}>
      <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between">
        <Link href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <svg width="28" height="16" viewBox="0 0 28 16" fill="none" aria-hidden="true">
            <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", letterSpacing: "-0.01em", color: NAVY, lineHeight: 1 }}>
            Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
          </span>
        </Link>
        <div className="flex items-center gap-6">
          <Link href="/blog" style={{ fontSize: "0.85rem", color: MUTED, textDecoration: "none" }}>← All articles</Link>
          <Link href="/check"
            style={{ background: NAVY, color: "#fff", textDecoration: "none", borderRadius: 999, padding: "8px 20px", fontSize: "0.85rem", fontWeight: 500 }}>
            Book a BizDr Check
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer style={{ background: NAVY, padding: "2rem 1.75rem", marginTop: "auto" }}>
      <div className="mx-auto max-w-6xl flex flex-wrap items-center justify-between gap-4">
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", color: "#fff" }}>
          Biz<em style={{ fontStyle: "italic", color: TEAL }}>Dr</em>
        </span>
        <div className="flex gap-5 flex-wrap">
          <Link href="/" style={{ fontSize: "0.8rem", color: "rgba(255,255,255,.4)", textDecoration: "none" }}>Home</Link>
          <Link href="/blog" style={{ fontSize: "0.8rem", color: "rgba(255,255,255,.4)", textDecoration: "none" }}>Blog</Link>
          <Link href="/privacy" style={{ fontSize: "0.8rem", color: "rgba(255,255,255,.4)", textDecoration: "none" }}>Privacy</Link>
        </div>
        <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,.3)" }}>© {year} BizDr. All rights reserved. · ABN 69 491 275 460</span>
      </div>
    </footer>
  );
}

export default function BlogPost() {
  const params = useParams<{ slug: string }>();
  const post = getPostBySlug(params.slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | BizDr`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", post.metaDescription);
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute("content", `${post.title} | BizDr`);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute("content", post.metaDescription);
    }
  }, [post]);

  if (!post) {
    return (
      <div style={{ background: CREAM, minHeight: "100vh", fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column" }}>
        <Nav />
        <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem 1.75rem" }}>
          <div style={{ textAlign: "center" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "2rem", color: NAVY, marginBottom: "1rem" }}>Article not found</h1>
            <Link href="/blog" style={{ color: TEAL, textDecoration: "none", fontWeight: 500 }}>← Back to all articles</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: CREAM, minHeight: "100vh", fontFamily: "'DM Sans', sans-serif", color: NAVY, display: "flex", flexDirection: "column" }}>
      <Nav />

      <main style={{ flex: 1 }}>
        {/* Header */}
        <section style={{ background: NAVY, padding: "4rem 1.75rem 3rem" }}>
          <div className="mx-auto max-w-2xl">
            <div style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: GOLD, marginBottom: "1rem" }}>
              Free Guide · BizDr
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: 500, color: "#fff", lineHeight: 1.25, margin: 0 }}>
              {post.title}
            </h1>
          </div>
        </section>

        {/* Content */}
        <article style={{ padding: "3rem 1.75rem" }}>
          <div className="mx-auto max-w-2xl">
            {post.sections.map((section, i) => (
              <div key={i} style={{ marginBottom: "2rem" }}>
                {section.heading && (
                  <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.25rem", fontWeight: 500, color: NAVY, marginBottom: "0.6rem", lineHeight: 1.35 }}>
                    {section.heading}
                  </h2>
                )}
                <p style={{ color: MUTED, lineHeight: 1.8, fontSize: "1rem", margin: 0 }}>
                  {section.text}
                </p>
              </div>
            ))}

            {/* Divider */}
            <div style={{ borderTop: "1px solid #e4ddd2", margin: "2.5rem 0" }} />

            {/* CTA */}
            <div style={{ background: "#fff", border: "1px solid #e4ddd2", borderRadius: 20, padding: "2rem", textAlign: "center" }}>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", fontWeight: 500, color: NAVY, marginBottom: "0.6rem" }}>
                Want help putting this into practice?
              </h3>
              <p style={{ color: MUTED, fontSize: "0.9rem", lineHeight: 1.7, marginBottom: "1.25rem" }}>
                A free BizDr Check takes 5 minutes. We'll review your business online and come back with honest, practical advice — no pitch, no pressure.
              </p>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
                <Link href="/check"
                  style={{ display: "inline-block", background: TEAL, color: "#fff", textDecoration: "none", borderRadius: 999, padding: "12px 28px", fontWeight: 500, fontSize: "0.9rem" }}>
                  Get a Free BizDr Check
                </Link>
                <Link href="/#contact"
                  style={{ display: "inline-block", background: "transparent", color: NAVY, textDecoration: "none", borderRadius: 999, padding: "12px 28px", fontWeight: 500, fontSize: "0.9rem", border: `1.5px solid ${NAVY}` }}>
                  Get in Touch
                </Link>
              </div>
            </div>

            {/* Back link */}
            <div style={{ marginTop: "2rem", textAlign: "center" }}>
              <Link href="/blog" style={{ color: TEAL, fontSize: "0.9rem", textDecoration: "none", fontWeight: 500 }}>
                ← Back to all articles
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
